/**
 * PATH: packages/ui/src/kanban/components/board-view/swimlaneActionHorizon.ts
 * FILE_ID: UI-KANBAN-UTL-117
 * PURPOSE: Split-by-Action-date forward calendar ladder + move-to-band presets.
 * ROLE IN ARCHITECTURE: Pure I→O for the 'action' swimlane dimension.
 *   Extracted from swimlaneGrouping.ts on contact (file was over the 500-line
 *   ceiling) so the empty-ladder contract can live next to the bucket math.
 * MAIN EXPORTS:
 *   - byActionHorizon: card → Overdue / day / week / month / residual bands
 *   - actionHorizonPresets: inverse dates for "move to band"
 *   - weekStartMs / isoLocal / ACTION_MS_DAY: seedPatchForBand helpers
 * NON-RESPONSIBILITIES: Does not render bands or PATCH cards.
 * NOTES FOR FUTURE AI: Near-term day+week bands ALWAYS emit, even at count 0.
 *   Those empty rows ARE the drop targets for cards with no schedule date
 *   (BUG-2026-08-31-SPLIT-ACTION-EMPTY-WEEK-LANES). Month bands stay
 *   occupancy-based (unbounded). Do not re-introduce `.filter(g => g.cardIds.size)`
 *   on the week ladder. A card with only due_date or start_date still bands
 *   here (BUG-2026-09-11-INBOX-DATE-ROUTING-HIDES-AI-CARDS) — action_date
 *   wins when both exist.
 */

import type { Card } from '../../types'
import {
  dayStartMs,
  emptyGroup,
  finalize,
  type SwimlaneGroup,
} from './swimlaneGroupUtil'

export const ACTION_MS_DAY = 86_400_000

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const WEEK_LABELS = ['This week', 'Next week', 'In 2 weeks', 'In 3 weeks', 'In 4 weeks']
const WEEK_COLORS = ['#3B82F6', '#3B82F6', '#6366F1', '#6366F1', '#8B5CF6']

/** Monday-based start-of-week ms for a local day-start `dayMs`. `Math.round`
 *  at the call site absorbs any ±1h drift across a DST boundary. */
export function weekStartMs(dayMs: number): number {
  const dow = (new Date(dayMs).getDay() + 6) % 7 // Mon=0 … Sun=6
  return dayMs - dow * ACTION_MS_DAY
}

/** Local-date ISO (YYYY-MM-DD) for a day-start ms — local, not UTC, so it
 *  matches the operator's calendar day (action dates are dates, not stamps). */
export function isoLocal(ms: number): string {
  const d = new Date(ms)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

export interface ActionHorizonPreset {
  label: string
  iso: string
}

/** Date that places a card on the action-horizon ladder.
 *
 * Action date wins (this IS the action split). When it is missing, fall
 * through to due then start so an AI-seeded inbox reminder is not stranded
 * in "No action date". */
function actionHorizonAnchor(
  card: Pick<Card, 'action_date' | 'due_date' | 'start_date'>,
): string | null | undefined {
  return card.action_date || card.due_date || card.start_date
}

/**
 * ACTION-DATE HORIZON LADDER — the Split=='action' bands. Unlike the coarse
 * 'due' deadline buckets, this gives `action_date` a PROPER forward calendar
 * structure so the migrated Trello time lanes (1 week / 2 weeks / 3 weeks /
 * 1 month…) read as distinct time bands instead of one giant "Later" blob
 * (operator: "where is proper week / day structure"). Ordered, non-overlapping:
 *   Overdue · Today · Tomorrow · This week · Next week · In 2/3/4 weeks ·
 *   <Month> bands (name, + year if not the current year) for anything 5+ weeks
 *   out · No action date (residual, pinned last).
 *
 * BUG-2026-08-31-SPLIT-ACTION-EMPTY-WEEK-LANES: the near-term day+week ladder
 * always renders, including empty bands. Empty week rows are the drop surface
 * that sets `action_date`. Dropping them hid the whole ladder on boards where
 * every card is still "No action date", and SplitBandNavigator hid the jump
 * chips (<2 bands). Month bands stay occupancy-based so empty months never
 * unbounded-grow the stack. Residual still only shows when it holds cards.
 * Membership uses action_date, then due_date, then start_date so an
 * AI-seeded inbox reminder is not stranded in the residual
 * (BUG-2026-09-11-INBOX-DATE-ROUTING-HIDES-AI-CARDS).
 */
export function byActionHorizon(cards: Card[], now: Date): SwimlaneGroup[] {
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const tomorrowMs = todayMs + ACTION_MS_DAY
  const thisWeekMs = weekStartMs(todayMs)

  const overdue = emptyGroup('action:overdue', 'Overdue', '#EF4444')
  const today = emptyGroup('action:today', 'Today', '#F59E0B')
  const tomorrow = emptyGroup('action:tomorrow', 'Tomorrow', '#FBBF24')
  const weeks = WEEK_LABELS.map((label, i) => emptyGroup(`action:w${i}`, label, WEEK_COLORS[i]))
  const months = new Map<number, SwimlaneGroup>()
  const residual = emptyGroup('residual', 'No action date', null)

  for (const c of cards) {
    const raw = actionHorizonAnchor(c)
    if (!raw) {
      residual.cardIds.add(c.id)
      continue
    }
    const dm = dayStartMs(raw)
    if (dm == null) {
      residual.cardIds.add(c.id)
      continue
    }
    if (dm < todayMs) {
      overdue.cardIds.add(c.id)
    } else if (dm === todayMs) {
      today.cardIds.add(c.id)
    } else if (dm === tomorrowMs) {
      tomorrow.cardIds.add(c.id)
    } else {
      const wOff = Math.round((weekStartMs(dm) - thisWeekMs) / (7 * ACTION_MS_DAY))
      if (wOff <= 4) {
        weeks[wOff].cardIds.add(c.id)
      } else {
        const d = new Date(dm)
        const key = d.getFullYear() * 12 + d.getMonth()
        let g = months.get(key)
        if (!g) {
          const label =
            d.getFullYear() === now.getFullYear()
              ? MONTHS[d.getMonth()]
              : `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
          g = emptyGroup(`action:m${key}`, label, '#14B8A6')
          months.set(key, g)
        }
        g.cardIds.add(c.id)
      }
    }
  }

  const monthBands = [...months.entries()].sort((a, b) => a[0] - b[0]).map(([, g]) => finalize(g))
  const out = [overdue, today, tomorrow, ...weeks].map(finalize)
  out.push(...monthBands)
  if (residual.cardIds.size) out.push(finalize(residual))
  return out
}

/**
 * The INVERSE of {@link byActionHorizon}: one representative `action_date` per
 * near-term band, so a card can be "moved to a band" in a couple of clicks
 * (operator: "moving cards across them is hard — there should be an easier
 * way") without a long drag across tall bands. Anchored on THIS ISO week's
 * Friday so each date lands squarely inside its band; a round-trip test pins
 * that (Today→Today … In 4 weeks→In 4 weeks). Months further out use the menu's
 * custom date input. "This week" is dropped Fri–Sun when the week is over.
 */
export function actionHorizonPresets(now: Date): ActionHorizonPreset[] {
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const dow = (new Date(todayMs).getDay() + 6) % 7 // Mon=0 … Sun=6
  const fridayMs = todayMs + (4 - dow) * ACTION_MS_DAY // Friday of the current ISO week

  const presets: ActionHorizonPreset[] = [
    { label: 'Today', iso: isoLocal(todayMs) },
    { label: 'Tomorrow', iso: isoLocal(todayMs + ACTION_MS_DAY) },
  ]
  // Offer "This week" only when this week's Friday is still a distinct future
  // day in the current week (i.e. today is Mon–Wed); otherwise it collapses
  // into Today/Tomorrow/Next week and would mislabel.
  if (fridayMs > todayMs + ACTION_MS_DAY) {
    presets.push({ label: 'This week', iso: isoLocal(fridayMs) })
  }
  presets.push(
    { label: 'Next week', iso: isoLocal(fridayMs + 7 * ACTION_MS_DAY) },
    { label: 'In 2 weeks', iso: isoLocal(fridayMs + 14 * ACTION_MS_DAY) },
    { label: 'In 3 weeks', iso: isoLocal(fridayMs + 21 * ACTION_MS_DAY) },
    { label: 'In 4 weeks', iso: isoLocal(fridayMs + 28 * ACTION_MS_DAY) },
  )
  return presets
}
